import type {
    DeviceSKUPayload,
    DeviceSKUResponse,
} from '@/types/apps/device-sku.type';
import type {
    ApiResponse,
    PaginatedResult,
    QueryParams,
} from '@/types/common/api.type';
import type { BaseApiClientInterface } from '@/types/service/base-api-client.interface';
import type { Creatable } from '@/types/service/creatable.interface';
import type { Deletable } from '@/types/service/deletable.interface';
import type { Exportable } from '@/types/service/exportable.interface';
import type { Importable } from '@/types/service/importable.interface';
import type { Indexable } from '@/types/service/indexable.interface';
import type { Showable } from '@/types/service/showable.interface';
import type { Updatable } from '@/types/service/updatable.interface';

export interface DeviceSKUServiceInterface {}

type DeviceSKUListResponse = ApiResponse<PaginatedResult<DeviceSKUResponse>>;

export class DeviceSKUService
    implements
        Showable,
        Updatable,
        Deletable,
        Creatable,
        Indexable,
        Importable,
        Exportable
{
    public constructor(private apiClient: BaseApiClientInterface) {}

    public endpoints = {
        base: () => '/api/device-sku',
        detail: (id: string) => `/api/device-sku/${id}`,
        import: () => '/api/device-sku/import',
        export: () => '/api/device-sku/export',
    };

    public index(params?: QueryParams): Promise<DeviceSKUListResponse> {
        return this.apiClient.fetchApi(this.endpoints.base(), params);
    }

    public detail(
        id: string,
        params?: QueryParams
    ): Promise<ApiResponse<DeviceSKUResponse>> {
        const url = this.endpoints.detail(id);

        return this.apiClient.fetchApi(url, params);
    }

    public create(
        payload: DeviceSKUPayload
    ): Promise<ApiResponse<DeviceSKUResponse>> {
        return this.apiClient.postApi(this.endpoints.base(), payload);
    }

    public update(
        id: string,
        payload: DeviceSKUPayload
    ): Promise<ApiResponse<DeviceSKUResponse>> {
        const url = this.endpoints.detail(id);

        return this.apiClient.putApi(url, payload);
    }

    public destroy(
        ids: string[],
        isHardDeleted: boolean = false
    ): Promise<ApiResponse<void>> {
        const url = this.endpoints.base();

        return this.apiClient.destroyApi<void>(url, ids, isHardDeleted);
    }

    public async importFile(file: File): Promise<any> {
        const form = this.apiClient.buildFormData({ file });

        return this.apiClient.postApi<any>(this.endpoints.import(), form);
    }

    public async exportFile(): Promise<Blob> {
        return this.apiClient.fetchBlob(this.endpoints.export());
    }
}
